import InvoiceType from "@/enums/invoiceType";

export default class CalculationsHelper {
  static round(amount, precision = 2) {
    if (amount === null || amount === undefined) return 0;
    return Number(parseFloat(amount).toFixed(precision));
  }

  static getType(invoice) {
    return invoice.type;
  }

  static getAmount(invoice, fraction = 100) {
    const amount = invoice.scoring?.amount ?? invoice.amount;
    return this.round(amount * fraction / 100);
  }

  static getAequitexFeeSellerPercentage(invoice) {
    return this.round(invoice.scoring?.fee_hive) ?? 0;
  }

  static getAequitexFeeSellerAmount(invoice, fraction = 100) {
    return this.round(this.getAequitexFeeSellerPercentage(invoice) * this.getAmount(invoice, fraction) / 100);
  }

  static getAequitexFeeInvestorPercentage(invoice) {
    return this.round(invoice.scoring?.investor_fee) ?? 0;
  }

  static getAequitexFeeInvestorAmount(invoice, fraction = 100) {
    return this.round(this.getAequitexFeeInvestorPercentage(invoice) * this.getAmount(invoice, fraction) / 100);
  }

  static getDiscountPercentage(invoice) {
    return this.round(invoice.scoring?.discount_in_percentage) ?? 0;
  }

  static getSellerDiscountPercentage(invoice) {
    return this.round(invoice.scoring?.discount_in_percentage - invoice.scoring?.investor_fee) ?? 0;
  }

  static getDiscountAmount(invoice, fraction = 100) {
    return this.round(this.getAmount(invoice, fraction) * this.getDiscountPercentage(invoice) / 100);
  }

  static getSellerDiscountAmount(invoice, fraction = 100) {
    return this.round(this.getAmount(invoice, fraction) * this.getSellerDiscountPercentage(invoice) / 100);
  }

  static getRefundPercentage(invoice) {
    return invoice.scoring?.refund_percentage ?? 0;
  }

  static getRefundAmount(invoice, fraction = 100) {
    return this.round(this.getAmount(invoice, fraction) * this.getRefundPercentage(invoice) / 100);
  }

  static getRefundDefaultPercentage(invoice) {
    return invoice.scoring?.refund_if_defaults ?? 0;
  }

  static getRefundDefaultAmount(invoice, fraction = 100) {
    return this.round(this.getAmount(invoice, fraction) * this.getRefundDefaultPercentage(invoice) / 100);
  }

  static getRoiPercentage(invoice) {
    switch (invoice.type) {
      case InvoiceType.NonRecourse:
      case InvoiceType.Recourse:
        return this.round(
          this.getDiscountPercentage(invoice)
          - this.getAequitexFeeSellerPercentage(invoice)
          - this.getAequitexFeeInvestorPercentage(invoice)
        );
      case InvoiceType.Reverse:
        return this.round(this.getRoiAmount(invoice) / this.getAmount(invoice) * 100);
      default:
        return null;
    }
  }

  static getRoiAmount(invoice, fraction = 100) {
    switch (invoice.type) {
      case InvoiceType.NonRecourse:
      case InvoiceType.Recourse:
        return this.round(this.getAmount(invoice, fraction) * this.getRoiPercentage(invoice) / 100);
      case InvoiceType.Reverse:
        return this.round(
          this.getDiscountAmount(invoice, fraction)
          - this.getAequitexFeeInvestorAmount(invoice, fraction)
          - this.getAequitexFeeSellerAmount(invoice, fraction)
        );
      default:
        return null;
    }
  }

  static getInvestorRoiAmount(invoice, fraction = 100) {
    const aequitexFeeInvestor = this.getAequitexFeeInvestorAmount(invoice, fraction);
    switch (invoice.type) {
      case InvoiceType.NonRecourse:
      case InvoiceType.Recourse:
        // console.log(this.getAequitexFeeInvestorAmount(invoice, fraction))
        return this.round(this.getRoiAmount(invoice, fraction) - aequitexFeeInvestor);
      case InvoiceType.Reverse:
        return this.round(this.getDiscountAmount(invoice, fraction) - aequitexFeeInvestor);
      default:
        return null;
    }
  }

  static getInvestorRoiPercentage(invoice) {
    const investorRoiAmount = this.getInvestorRoiAmount(invoice);
    const investmentAmount = this.getInvestmentAmount(invoice);
    return this.round(investorRoiAmount / investmentAmount * 100);
  }

  static getInvestmentAmount(invoice, fraction = 100) {
    switch (invoice.type) {
      case InvoiceType.NonRecourse:
      case InvoiceType.Recourse:
        return this.round(this.getAmount(invoice, fraction) - this.getInvestorRoiAmount(invoice, fraction));
      case InvoiceType.Reverse:
        return this.round(this.getAmount(invoice, fraction) + this.getAequitexFeeInvestorAmount(invoice, fraction));
      default:
        return null;
    }
  }

  static getLiquidityAmount(invoice) {
    switch (invoice.type) {
      case InvoiceType.NonRecourse:
      case InvoiceType.Recourse:
        return this.round(
          this.getAmount(invoice)
          - this.getDiscountAmount(invoice)
          + this.getAequitexFeeInvestorAmount(invoice)
          - this.getRefundAmount(invoice)
          - this.getRefundDefaultAmount(invoice)
        );
      case InvoiceType.Reverse:
        return this.round(
          this.getAmount(invoice)
          - this.getRefundAmount(invoice)
          - this.getRefundDefaultAmount(invoice)
          - this.getAequitexFeeSellerAmount(invoice)
        );
      default:
        return null;
    }
  }

  static getInvestorRepaymentAmount(invoice, fraction = 100) {
    return this.round((this.getAmount(invoice) + this.getDiscountAmount(invoice)) * fraction / 100);
  }

  static getInvestorSettledAmount(invoice, fraction = 100) {
    switch (invoice.type) {
      case InvoiceType.NonRecourse:
      case InvoiceType.Recourse:
        return this.round(fraction * this.getAmount(invoice) / 100);
      case InvoiceType.Reverse:
        return this.round(fraction * (this.getAmount(invoice) + this.getDiscountAmount(invoice)) / 100);
      default:
        return null;
    }
  }

  static getInvestorVoluntaryAmount(invoice, fraction = 100) {
    const fractionRefund = (this.getRefundAmount(invoice) * fraction / 100);
    const currentDate = new Date();
    const dueDate = new Date(invoice.due_date);

    const dueDatePlus7Days = new Date(dueDate.getTime() + (7 * 24 * 60 * 60 * 1000));

    let refund;
    if (currentDate > dueDatePlus7Days) {
      refund = fractionRefund / 2;
    } else {
      refund = fractionRefund / 3;
    }

    return this.round(refund);
  }

  static getInvestorDefaultVoluntaryPercent(invoice) {
    return this.round(invoice.scoring?.investor_voluntary_compensation ?? 0);
  }

  static getInvestorDefaultVoluntaryAmount(invoice, fraction = 100) {
    return this.round((this.getInvestorDefaultVoluntaryPercent(invoice) * this.getRefundDefaultAmount(invoice) / 100) * fraction / 100);
  }

  static getInvestorDefaultAmount(invoice, fraction) {
    return this.round(fraction * this.getInvestorDefaultVoluntaryAmount(invoice) / 100);
  }

  static getSellerSettledAmount(invoice, fraction = 100) {
    return this.round((this.getRefundAmount(invoice) + this.getRefundDefaultAmount(invoice) * fraction / 100));
  }

  static getRepaymentAmount(invoice, fraction = 100)
  {
    return this.round((this.getAmount(invoice) + this.getDiscountAmount(invoice)) * fraction / 100);
  }
}
